import { action, makeObservable, observable } from "mobx";
import { uid } from "uid";
import type { ReactNode } from "react";

export type Snack = {
  id: string;
  type: "info" | "warning" | "error" | "success";
  flash?: boolean;
  showProgress?: boolean;
  anchorOrigin: {
    vertical: "top" | "bottom";
    horizontal: "left" | "right" | "center";
  };
  onClose?: () => void;
  message: ReactNode;
  autoHideDuration: number | null;
};

const defaultSnack = {
  type: "info",
  flash: false,
  anchorOrigin: {
    vertical: "top",
    horizontal: "center",
  },
  autoHideDuration: 5000,
  message: "",
} as const;

export class SnackbarStore {
  @observable
  snacks: Snack[] = [];

  progressInterval: Record<string, NodeJS.Timeout> = {};
  progressStartTimestamp: Record<string, number> = {};
  pausedProgress: Record<string, number> = {};
  additionalTime: Record<string, number> = {};

  @observable
  progress: Record<string, number> = {};

  constructor() {
    makeObservable(this);
  }

  @action
  pauseProgress(id: string) {
    this.pausedProgress[id] = Date.now();
  }

  @action
  unpauseProgress(id: string) {
    if (this.pausedProgress[id]) {
      const diff = Date.now() - this.pausedProgress[id];
      this.additionalTime[id] = diff + (this.additionalTime[id] || 0);
    }
    delete this.pausedProgress[id];
  }

  @action
  open(props: Partial<Snack>) {
    const { flash = false } = props;
    if (flash) {
      this.snacks
        .filter((snack) => snack.flash)
        .forEach((snack: Snack) => {
          if (this.progressInterval[snack.id]) {
            clearInterval(this.progressInterval[snack.id]);
          }

          this.close(snack.id);
        });
    }

    const snack = {
      id: uid(),
      ...defaultSnack,
      ...props,
    };

    this.snacks.push(snack);
    if (snack.autoHideDuration) {
      const duration = snack.autoHideDuration;
      this.progressStartTimestamp[snack.id] = Date.now();
      this.progressInterval[snack.id] = setInterval(() => {
        if (this.pausedProgress[snack.id]) {
          return;
        }

        const diff =
          Date.now() -
          (this.additionalTime[snack.id] ?? 0) -
          this.progressStartTimestamp[snack.id];

        const progress = (diff / duration) * 100;
        this.setProgress(snack.id, progress);

        if (progress >= 100) {
          this.close(snack.id);
        }
      }, 100);
    }
    return snack;
  }

  @action
  setProgress(id: string, progress: number) {
    this.progress[id] = progress;
  }

  @action
  error(props: Omit<Partial<Snack>, "type">) {
    const { flash = false } = props;
    return this.open({
      ...defaultSnack,
      ...(!flash && {
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      }),
      type: "error",
      ...props,
    });
  }

  @action
  info(props: Omit<Partial<Snack>, "type">) {
    return this.open({
      ...defaultSnack,
      type: "info",
      ...props,
    });
  }

  @action
  success(props: Omit<Partial<Snack>, "type">) {
    return this.open({
      ...defaultSnack,
      type: "success",
      ...props,
    });
  }

  @action
  warning(props: Omit<Partial<Snack>, "type">) {
    return this.open({
      ...defaultSnack,
      type: "warning",
      ...props,
    });
  }

  @action
  close(id: string) {
    const snackIndex = this.snacks.findIndex((snack) => snack.id === id);
    if (snackIndex !== -1) {
      const snack = this.snacks[snackIndex];
      snack?.onClose?.();
      this.snacks.splice(snackIndex, 1);
    }

    delete this.progressStartTimestamp[id];
    delete this.progress[id];
    delete this.additionalTime[id];
    delete this.pausedProgress[id];

    if (this.progressInterval[id]) {
      clearInterval(this.progressInterval[id]);
    }
  }
}
